import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { DeleteOutlined, EditOutlined, LinkOutlined, QrcodeOutlined } from '@ant-design/icons';
import { Avatar, Button, List, Popconfirm, Popover, Result, Space, Tag, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'umi';
import { deleteBookingPageRequest } from '@/services/api/bookingPage';
import { BookingPageModel } from '@/typings/models/BookingPage';
import { useDefaultLeadSourceConfiguration, usePreviouslySelectedSolution } from '@/utils/hooks';
import styles from './index.less';
import { usePubSub } from '@/context/PubSubContext';
import { FETCH_ME } from '@/context/PubSubContext/events';
import { getBookingPageLink } from '@/components/BookingPage/utils';
import QRCodeViewer from '@/components/QRCodeViewer';

const { Text, Paragraph } = Typography;
const TRACKING_LABEL = 'BookingPages';

interface BookingPagesListProps {
  onEdit: (bookingPage: BookingPageModel) => void;
  onRefresh?: () => void;
  toggleDrawer?: () => void;
  list: BookingPageModel[];
  userId: string;
}

const BookingPagesList: React.FC<BookingPagesListProps> = ({
  onEdit,
  onRefresh,
  list,
  userId,
  toggleDrawer,
}) => {
  const { publish } = usePubSub();
  const defaultLeadSourceConfiguration = useDefaultLeadSourceConfiguration();
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  usePreviouslySelectedSolution();

  useEffect(() => {
    if (userId) fetchTableData();
  }, [userId]);

  function fetchTableData() {
    if (!userId) return;
    publish(FETCH_ME);
    if (onRefresh) onRefresh();
  }

  function handleDeleteBookingPage(bookingLinkId: string) {
    if (bookingLinkId != 'null' && bookingLinkId !== undefined) {
      return deleteBookingPageRequest({
        params: { bookingLinkId },
      }).then(() => {
        fetchTableData();
      });
    }
  }

  function handleVisitLink(link: string) {
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  if (!defaultLeadSourceConfiguration.base_url)
    return (
      <Result
        status="info"
        title={
          <F
            id="pages.profile.bookingPages.noWidget.title"
            defaultMessage="No booking widget configured"
          />
        }
        subTitle={
          <F
            id="pages.profile.bookingPages.noWidget.subTitle"
            defaultMessage="It seems you don't have a booking widget configured for your account. Please contact support to request one."
          />
        }
        extra={
          <Button type="primary" onClick={() => {
            window.location.replace("/support");
          }}>
            <F
              id="pages.profile.bookingPages.noWidget.button"
              defaultMessage="Contact Support"
            />
          </Button>
        }
      />
    );

  return (
    <>
      <List
        itemLayout="horizontal"
        loading={loading}
        className={styles.bookingPageListContainer}
        dataSource={list}
        size={'small'}
        renderItem={(bookingPage) => {
          const bookingLink = getBookingPageLink(
            bookingPage,
            defaultLeadSourceConfiguration,
          );
          const linkContent = (
            <Paragraph
              code
              ellipsis={{ rows: 1 }}
              copyable={{
                text: bookingLink,
              }}
              className={styles.bookingPageLink}
            >
              {bookingLink}
            </Paragraph>
          );
          return (
            <List.Item
              actions={[
                <Popover
                  arrow={false}
                  showArrow={false}
                  trigger={['click']}
                  destroyOnHidden
                  content={
                    <QRCodeViewer
                      description={linkContent}
                      value={bookingLink}
                      size={86}
                      mode="image"
                      bordered={false}
                    />
                  }
                >
                  <Button icon={<QrcodeOutlined />} type={'link'} />
                </Popover>,
                <Button
                  type="link"
                  icon={<LinkOutlined />}
                  onClick={() => handleVisitLink(bookingLink)}
                />,
                <Tooltip
                  title={<F id="common.actions.edit" defaultMessage="Edit" />}
                >
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(bookingPage)}
                  />
                </Tooltip>,
                <Popconfirm
                  onConfirm={() => handleDeleteBookingPage(bookingPage.id)}
                  placement={'topRight'}
                  title={
                    <F
                      entity={INTL_ENTITY.BOOKING_PAGE}
                      action={INTL_ACTION.REMOVE_CONFIRMATION}
                    />
                  }
                >
                  <Button type={'link'} icon={<DeleteOutlined />} danger />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar shape={'square'}>
                    <LinkOutlined />
                  </Avatar>
                }
                title={
                  <Space size={16}>
                    <Text strong>{bookingPage.name}</Text>
                    {bookingPage.description && (
                      <Text type={'secondary'}>
                        <small>{bookingPage.description}</small>
                      </Text>
                    )}
                  </Space>
                }
                description={
                  <Space direction={'vertical'}>
                    <Paragraph
                      style={{ maxWidth: 350, margin: 0 }}
                      copyable
                      code
                      ellipsis={{ rows: 1 }}
                    >
                      {getBookingPageLink(
                        bookingPage,
                        defaultLeadSourceConfiguration,
                      )}
                    </Paragraph>
                    <Space wrap size={0}>
                      {bookingPage.branch_services &&
                      bookingPage.branch_services.length > 0 ? (
                        <>
                          {bookingPage.branch_services
                            .slice(0, 3)
                            .map((serviceType) => (
                              <Tag
                                key={serviceType.uuid}
                                bordered={false}
                                color="default"
                              >
                                {serviceType.name}
                              </Tag>
                            ))}
                          {bookingPage.branch_services.length > 3 && (
                            <Tag color="default" bordered={false}>
                              +{bookingPage.branch_services.length - 3}{' '}
                              <F
                                id={'pages.leads.filters.more'}
                                defaultMessage={'More'}
                              />
                            </Tag>
                          )}
                        </>
                      ) : (
                        <Text type="secondary">
                          <F
                            entity={INTL_ENTITY.SERVICE}
                            action={INTL_ACTION.ALL}
                          />
                        </Text>
                      )}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />
    </>
  );
};

export default BookingPagesList;
